import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, CheckCircle2, Sparkles, Infinity as InfinityIcon, Camera, Dumbbell, Activity,
  ShieldCheck, MapPin, Footprints, Apple, Lock, FlaskConical, Venus, Droplets, Gauge, Scale,
  HeartPulse, Watch, Network, BookOpen, Plus,
} from "lucide-react";
import { TuhaWordmark } from "@/components/TuhaLogo";
import LandingQuiz from "@/components/LandingQuiz";
import { startGoogleLogin } from "@/lib/oauth";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const PLAN_ORDER = ["trial", "monthly", "yearly", "lifetime"];
const PLAN_BADGE = {
  trial: "Start here · free",
  monthly: "Most popular",
  yearly: "Best value",
  lifetime: "Pay once · keep forever",
};
const detectCurrency = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (/Kolkata|Calcutta/i.test(tz)) return "INR";
    if ((navigator.language || "").toLowerCase().endsWith("-in")) return "INR";
    return "USD";
  } catch (_) { return "INR"; }
};
// Static fallback so the grid ALWAYS renders, even if /billing/plans is slow or blocked.
const STATIC_PLANS = {
  INR: {
    trial:    { name: "3-Day Trial", kind: "trial",        price: "₹0",      period: "3 days free" },
    monthly:  { name: "Monthly",     kind: "subscription", regular_price: "₹799",    period: "month" },
    yearly:   { name: "Yearly",      kind: "subscription", regular_price: "₹5,699",  period: "year" },
    lifetime: { name: "Lifetime",    kind: "one_time",     price: "₹23,999", period: "one-time" },
  },
  USD: {
    trial:    { name: "3-Day Trial", kind: "trial",        price: "$0",      period: "3 days free" },
    monthly:  { name: "Monthly",     kind: "subscription", regular_price: "$7.99",  period: "month" },
    yearly:   { name: "Yearly",      kind: "subscription", regular_price: "$90",    period: "year" },
    lifetime: { name: "Lifetime",    kind: "one_time",     price: "$270",    period: "one-time" },
  },
};
const PLAN_FEATURES = [
  "Personalized, condition-aware nutrition",
  "Condition-aware, clinically guided meal planning",
  "Macro, calorie, fiber & micronutrient tracking",
  "Multi-agentic AI personalization",
  "Evidence-informed nutrition for your biology",
];

const CONDITIONS = [
  { icon: Venus, label: "PCOS / PCOD", note: "Insulin-resistance aware nutrition" },
  { icon: Droplets, label: "Type 2 Diabetes", note: "Lower-GI, blood-sugar conscious plates" },
  { icon: Gauge, label: "Prediabetes", note: "Early, preventive lifestyle support" },
  { icon: Scale, label: "Obesity", note: "Sustainable, personalized fat-loss" },
  { icon: Activity, label: "Fatty Liver (NAFLD)", note: "Higher-fiber, lower saturated-fat focus" },
  { icon: HeartPulse, label: "Metabolic Syndrome", note: "Whole-pattern metabolic health" },
];

const AGENTS = [
  { icon: Apple, name: "Nutrition Agent", desc: "Designs balanced, condition-aware meals." },
  { icon: ShieldCheck, name: "Clinical-Safety Agent", desc: "Screens for conditions, allergies & medications." },
  { icon: MapPin, name: "Local & Seasonal Agent", desc: "Adapts to what's available where you live." },
  { icon: Footprints, name: "Activity Agent", desc: "Aligns nutrition with how you move." },
];

const STEPS = [
  { n: "01", t: "Assess", d: "Share your health history, biology, preferences and goals — once." },
  { n: "02", t: "Analyze", d: "KAAYA maps your profile against evidence-based nutrition science." },
  { n: "03", t: "Personalize", d: "Receive clinically guided, everyday meal guidance tailored to you." },
  { n: "04", t: "Adapt", d: "Log meals in a single chat — your plan adjusts as your data builds." },
];

const PREVIEWS = [
  {
    icon: Venus,
    tag: "PCOS · personalized",
    q: "What should I eat for my PCOS today?",
    title: "Higher-protein, lower-GI plate",
    body: "100g paneer bhurji · 2 millet rotis · 150g sautéed greens — built around insulin sensitivity.",
  },
  {
    icon: Droplets,
    tag: "Type 2 Diabetes · from photo",
    q: "Is this okay with my blood sugar?",
    title: "Moderate-GI — a small swap helps",
    body: "Swap white rice for 80g cooked quinoa to steady your blood-sugar response.",
  },
  {
    icon: Activity,
    tag: "Fatty Liver · evidence-informed",
    q: "Plan a liver-friendly dinner.",
    title: "Grilled fish & sautéed greens",
    body: "60g grilled fish · 200g sautéed greens — lower saturated fat, higher fiber for NAFLD-supportive eating.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5, ease: "easeOut" },
};

export default function Landing() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [currency] = useState(detectCurrency);
  const [plans, setPlans] = useState(STATIC_PLANS[detectCurrency()] || STATIC_PLANS.INR);
  useEffect(() => { if (!loading && user) nav("/chat", { replace: true }); }, [loading, user, nav]);
  useEffect(() => {
    api.get(`/billing/plans?currency=${currency}`)
      .then(({ data }) => { if (data?.plans && Object.keys(data.plans).length) setPlans(data.plans); })
      .catch(() => {});
  }, [currency]);

  return (
    <div className="min-h-screen bg-background grain">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between max-w-7xl mx-auto">
        <TuhaWordmark size="md" />
        <div className="flex items-center gap-2">
          <a href="#how" className="hidden sm:inline-flex items-center px-4 h-9 rounded-full text-sm text-foreground/80 hover:bg-muted transition" data-testid="header-how-link">How it works</a>
          <a href="#plans" className="hidden sm:inline-flex items-center px-4 h-9 rounded-full text-sm text-foreground/80 hover:bg-muted transition" data-testid="header-plans-link">Plans</a>
          <Button onClick={startGoogleLogin} data-testid="header-signin-btn" variant="ghost" className="rounded-full">Sign in</Button>
        </div>
      </header>

      {/* 1 — HERO · What is KAAYA? */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-16 text-center">
        <motion.div {...fadeUp} className="inline-flex items-center gap-2 rounded-full border border-secondary/30 bg-secondary/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-secondary mb-7" data-testid="hero-overline">
          <Sparkles size={13} /> Personalized nutrition, powered by AI
        </motion.div>
        <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }} className="font-serif-display text-5xl sm:text-6xl lg:text-7xl tracking-tight text-foreground leading-[1.02]">
          Your body, <span className="bg-gradient-to-r from-tuha-coral to-tuha-warm bg-clip-text text-transparent">in balance</span>.
        </motion.h1>
        <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-7 text-lg text-foreground/75 max-w-2xl mx-auto leading-relaxed">
          KAAYA learns your health history, biology, preferences and daily life to give you
          personalized, evidence-informed nutrition — tuned to how your body actually works.
        </motion.p>
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }} className="mt-9 flex flex-col items-center gap-3">
          <Button onClick={startGoogleLogin} data-testid="hero-cta-signin" size="lg" className="rounded-full px-8 h-12 bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20">
            Continue with Google <ArrowRight size={18} className="ml-2" />
          </Button>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Lock size={12} /> Clinically guided meal planning · Secure &amp; private
          </p>
        </motion.div>
      </section>

      {/* 60-second condition quiz — conversion driver */}
      <LandingQuiz onStart={startGoogleLogin} />

      {/* 2 — WHO IS IT FOR? */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-overline mb-2">who it's for</div>
          <h2 className="font-serif-display text-3xl md:text-5xl text-primary tracking-tight">Designed around your unique biology.</h2>
          <p className="mt-4 text-foreground/70">KAAYA personalizes nutrition for people managing — or working to prevent — lifestyle-related conditions.</p>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="conditions-grid">
          {CONDITIONS.map(({ icon: Icon, label, note }, i) => (
            <motion.div key={label} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.05 }}
              className="rounded-3xl bg-surface-elevated border border-border p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`condition-${i}`}>
              <div className="h-11 w-11 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                <Icon size={20} className="text-primary" />
              </div>
              <div className="font-serif-display text-2xl text-foreground">{label}</div>
              <div className="text-sm text-foreground/65 mt-1">{note}</div>
            </motion.div>
          ))}
        </div>
        <div className="flex justify-center mt-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-4 py-2 text-sm font-medium text-foreground/80">
            <ShieldCheck size={15} className="text-secondary" /> …and everyday metabolic prevention
          </span>
        </div>
      </section>

      {/* 3 — WHY DIFFERENT FROM CALORIE TRACKERS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div {...fadeUp}>
            <div className="text-overline mb-2">why it's different</div>
            <h2 className="font-serif-display text-3xl md:text-5xl text-primary tracking-tight leading-tight">Calorie trackers count.<br /> KAAYA understands.</h2>
            <p className="mt-5 text-foreground/75 leading-relaxed">
              A number to hit isn't a plan. KAAYA analyzes your medical history, dietary preferences, activity
              and local conditions to generate personalized nutrition grounded in evidence-based
              nutrition science — not generic targets.
            </p>
            <ul className="mt-6 space-y-3">
              {["Condition-aware, not one-size-fits-all", "Understands your biology & history", "Adapts to your kitchen, region & season", "Clinically guided, everyday recommendations"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-foreground/85">
                  <CheckCircle2 size={18} className="text-primary mt-0.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="relative">
            <div className="absolute -inset-6 bg-secondary/10 rounded-[2rem] blur-2xl -z-10"></div>
            <div className="rounded-3xl overflow-hidden border border-border shadow-sm bg-surface-elevated">
              <img src="/tuha-before-after.png" alt="Generic calorie tracking vs personalized KAAYA guidance" loading="lazy" className="w-full h-auto block" data-testid="before-after-img" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* 3b — SEE IT IN ACTION (reframed chat previews) */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-overline mb-2">in your words</div>
          <h2 className="font-serif-display text-3xl md:text-5xl text-primary tracking-tight">KAAYA personalizes — instantly.</h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="chat-preview-cards">
          {PREVIEWS.map(({ icon: Icon, tag, q, title, body }, i) => (
            <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.06 }}
              className="rounded-3xl bg-surface-elevated border border-border p-5 shadow-sm">
              <div className="text-right text-sm text-foreground/90 mb-3">&ldquo;{q}&rdquo;</div>
              <div className="rounded-2xl bg-surface p-4 border border-border">
                <div className="text-overline text-secondary mb-1 flex items-center gap-1.5"><Icon size={12} /> {tag}</div>
                <div className="font-serif-display text-xl text-primary">{title}</div>
                <div className="text-sm text-foreground/70 mt-1">{body}</div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                  <button onClick={startGoogleLogin} data-testid={`preview-recipe-${i}`} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-elevated px-3 py-1.5 text-xs font-medium text-foreground/80 hover:border-secondary/40 active:scale-95 transition">
                    <BookOpen size={13} className="text-secondary" /> View recipe
                  </button>
                  <button onClick={startGoogleLogin} data-testid={`preview-log-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-secondary/10 border border-secondary/30 px-3 py-1.5 text-xs font-medium text-secondary hover:bg-secondary/15 active:scale-95 transition">
                    <Plus size={13} /> Log meal
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 4 — HOW IT WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20 scroll-mt-20">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-overline mb-2">how it works</div>
          <h2 className="font-serif-display text-3xl md:text-5xl text-primary tracking-tight">From your biology to your plate.</h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map(({ n, t, d }, i) => (
            <motion.div key={n} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.05 }}
              className="rounded-3xl bg-surface-elevated border border-border p-6" data-testid={`step-${i}`}>
              <div className="font-serif-display text-5xl text-secondary/40">{n}</div>
              <div className="font-serif-display text-2xl text-foreground mt-2">{t}</div>
              <div className="text-sm text-foreground/65 mt-2 leading-relaxed">{d}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 5 — MULTI-AGENTIC FRAMEWORK (navy band) */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16">
        <div className="relative overflow-hidden rounded-[2rem] bg-foreground text-soft-white px-8 md:px-14 py-12 md:py-16" data-testid="multiagent-band">
          <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-primary/20 blur-3xl"></div>
          <div className="absolute -left-10 -bottom-12 w-56 h-56 rounded-full bg-secondary/20 blur-3xl"></div>
          <div className="relative">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-secondary mb-5">
                <Network size={14} /> how AI personalizes
              </div>
              <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.08]">
                A multi-agentic
                <span className="bg-gradient-to-r from-tuha-coral to-tuha-warm bg-clip-text text-transparent"> clinical brain.</span>
              </h2>
              <p className="mt-5 text-base md:text-lg text-soft-white/75 leading-relaxed">
                Specialised agents — for nutrition, clinical safety, local & seasonal context and activity —
                collaborate behind the scenes, so every recommendation is personalised for
                <span className="text-soft-white font-semibold"> you and your health.</span>
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
              {AGENTS.map(({ icon: Icon, name, desc }, i) => (
                <motion.div key={name} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.07 }}
                  className="rounded-2xl border border-soft-white/10 bg-soft-white/5 backdrop-blur-md p-5 hover:bg-soft-white/10 transition-colors" data-testid={`agent-${i}`}>
                  <Icon size={20} className="text-secondary" />
                  <div className="font-semibold mt-3">{name}</div>
                  <div className="text-sm text-soft-white/65 mt-1 leading-relaxed">{desc}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 6 — WHY TRUST IT */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-14 md:py-20 text-center">
        <motion.div {...fadeUp}>
          <div className="text-overline mb-2">why trust KAAYA</div>
          <h2 className="font-serif-display text-3xl md:text-5xl text-primary tracking-tight">Evidence-informed. Private by design.</h2>
        </motion.div>
        <div className="mt-10 grid sm:grid-cols-3 gap-4 text-left">
          {[
            { icon: FlaskConical, t: "Built on science", d: "Grounded in evidence-based nutrition science — not fads." },
            { icon: Lock, t: "Private & secure", d: "Your health data is protected with strict privacy and security." },
            { icon: HeartPulse, t: "Clinically guided", d: "A condition-aware approach to everyday, sustainable wellness." },
          ].map(({ icon: Icon, t, d }, i) => (
            <motion.div key={t} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.06 }}
              className="rounded-3xl bg-surface-elevated border border-border p-6" data-testid={`trust-${i}`}>
              <Icon size={20} className="text-primary" />
              <div className="font-semibold text-foreground mt-3">{t}</div>
              <div className="text-sm text-foreground/65 mt-1 leading-relaxed">{d}</div>
            </motion.div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-8 max-w-xl mx-auto">
          KAAYA offers personalized, evidence-informed nutrition guidance to support healthier outcomes.
          It is not a medical device and does not diagnose, treat, or cure any condition. Always consult
          your physician for medical decisions.
        </p>
      </section>

      {/* 7 — CONDITIONS & GOALS SUPPORTED */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-8">
          <div className="text-overline mb-2">supported today</div>
          <h2 className="font-serif-display text-3xl md:text-5xl text-primary tracking-tight">Conditions &amp; goals KAAYA supports.</h2>
        </motion.div>
        <div className="flex flex-wrap justify-center gap-3" data-testid="supported-chips">
          {["PCOS / PCOD", "Type 2 Diabetes", "Prediabetes", "Obesity", "Fatty Liver (NAFLD)", "Metabolic Syndrome",
            "Weight loss", "Shred fat / get lean", "Body recomposition", "Build muscle", "Heart health", "Everyday prevention"].map((c) => (
            <span key={c} className="inline-flex items-center gap-2 rounded-full border border-border bg-surface-elevated px-5 py-2.5 text-sm md:text-base font-medium text-foreground/85 hover:border-primary/30 transition-colors">
              <CheckCircle2 size={15} className="text-primary" /> {c}
            </span>
          ))}
        </div>
      </section>

      {/* 8 — FUTURE VISION */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-overline mb-2">on the horizon</div>
          <h2 className="font-serif-display text-3xl md:text-5xl text-primary tracking-tight">The future of KAAYA.</h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {[
            { icon: Dumbbell, t: "Workout Planner", d: "Recovery-aware workouts that adapt to your condition, body and goals." },
            { icon: Watch, t: "Wearable Integrations", d: "Sync steps, sleep, heart rate & energy to close your metabolic loop." },
          ].map(({ icon: Icon, t, d }, i) => (
            <motion.div key={t} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="rounded-[2rem] border-2 border-dashed border-border p-8 bg-transparent" data-testid={`future-${i}`}>
              <div className="flex items-center justify-between">
                <Icon size={24} className="text-secondary" />
                <span className="text-xs font-semibold uppercase tracking-wider text-foreground/50 border border-dashed border-border rounded-full px-3 py-1">Coming soon</span>
              </div>
              <div className="font-serif-display text-2xl text-foreground mt-4">{t}</div>
              <div className="text-sm text-foreground/65 mt-1 leading-relaxed">{d}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PLANS */}
      <section id="plans" className="max-w-7xl mx-auto px-6 md:px-12 py-16 scroll-mt-20">
        <div className="text-overline mb-2">simple, time-based access</div>
        <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-primary tracking-tight mb-3 md:whitespace-nowrap">
          Start free. Stay for as long as it serves you.
        </h2>
        <p className="text-foreground/70 max-w-2xl mb-4">
          Every account begins with a <strong>3-day free trial</strong> of full access — no card
          required. Best results come from using KAAYA daily so your data builds up and the
          personalization gets sharper.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLAN_ORDER.map((k) => {
            const p = plans[k];
            if (!p) return null;
            const highlighted = k === "monthly";
            const isTrial = k === "trial";
            const isSub = p.kind === "subscription";
            const hasIntro = isSub && !!p.intro_price;
            const bigPrice = isSub ? (hasIntro ? p.intro_price : p.regular_price) : p.price;
            const subline = isTrial
              ? "full access, no card"
              : isSub
              ? (hasIntro
                  ? `first ${p.period}, then ${p.regular_price}/${p.period === "month" ? "mo" : "yr"}`
                  : `${p.regular_price}/${p.period === "month" ? "mo" : "yr"}, auto-renews`)
              : "one-time payment";
            return (
              <div
                key={k}
                className={`relative rounded-3xl border p-5 bg-surface-elevated transition-all duration-300 hover:-translate-y-1 flex flex-col ${
                  highlighted ? "border-secondary shadow-md ring-1 ring-secondary/30" : "border-border"
                }`}
                data-testid={`landing-plan-${k}`}
              >
                <div className="text-overline text-secondary min-h-[18px]">{PLAN_BADGE[k]}</div>
                <div className="font-serif-display text-2xl text-primary mt-1">{p.name}</div>
                <div className="font-serif-display text-3xl text-primary mt-2 flex items-baseline gap-1">
                  {bigPrice}
                  {k === "lifetime" && <InfinityIcon size={18} className="text-secondary" />}
                </div>
                <div className="text-xs text-muted-foreground">{subline}</div>
                {k === "lifetime" && (
                  <div className="mt-2 inline-flex items-start gap-1 self-start rounded-2xl bg-secondary/15 px-2.5 py-1.5 text-[11px] font-semibold leading-snug text-secondary">
                    <Sparkles size={11} className="mt-0.5 shrink-0" /> Limited access — for serious, health-conscious individuals only!
                  </div>
                )}
                <ul className="mt-4 space-y-2 text-sm flex-1">
                  {(isTrial ? PLAN_FEATURES.slice(0, 3) : PLAN_FEATURES).map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 size={14} className="text-primary mt-0.5 shrink-0" />
                      <span className="text-foreground/85">{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={startGoogleLogin}
                  className={`w-full mt-5 rounded-full ${
                    highlighted ? "bg-secondary hover:bg-secondary/90" : "bg-primary hover:bg-primary/90"
                  }`}
                  data-testid={`landing-plan-cta-${k}`}
                >
                  {isTrial ? "Start 3-day free trial" : `Get ${p.name}`}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-6 max-w-2xl">
          Payments are processed securely by Razorpay ({currency}). Monthly &amp; yearly plans
          auto-renew — cancel anytime. The introductory price applies to your first cycle only.
          Lifetime access lasts as long as KAAYA (the company and product) remains live.
        </p>
      </section>

      <footer className="border-t border-border bg-surface mt-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex flex-col items-center gap-2 text-sm text-muted-foreground text-center">
          <TuhaWordmark size="sm" />
          <div className="text-xs text-foreground/70">KAAYA — your body, in balance</div>
          <div className="text-xs text-muted-foreground/80 max-w-xl mt-1">
            KAAYA provides personalized, evidence-informed nutrition guidance and does not diagnose,
            treat, or cure any medical condition. Consult your physician for medical decisions.
          </div>
          <div>© KAAYA 2026 · All rights reserved.</div>
          <div className="flex items-center gap-4 mt-1 text-xs">
            <a href="/terms" className="hover:text-foreground transition" data-testid="footer-terms-link">Terms &amp; Conditions</a>
            <a href="/privacy" className="hover:text-foreground transition" data-testid="footer-privacy-link">Privacy Policy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
